import React, { useState, useEffect, useCallback } from 'react';

// Import refactored components
import LoginScreen from './components/auth/LoginScreen';
import ProjectList from './components/projects/ProjectList';
import { AlertProvider } from './components/common/AppAlert';

// API Configuration
const API_BASE = '/api';

// API Helper Functions
const api = {
  get: async (endpoint, token = null) => {
    console.log('=== API GET DEBUG ===');
    console.log('Endpoint:', endpoint);
    console.log('Token provided:', !!token);
    
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      console.log('Authorization header set');
    } else {
      console.log('No token provided to API call');
    }
    
    console.log('Making fetch request to:', `${API_BASE}${endpoint}`);
    console.log('Headers:', headers);
    
    const response = await fetch(`${API_BASE}${endpoint}`, { headers });
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      console.error('Response not ok:', response.status, response.statusText);
      throw new Error(`HTTP ${response.status}`);
    }
    
    const refreshedToken = response.headers.get('X-Refreshed-Token');
    if (refreshedToken) localStorage.setItem('gtrack_token', refreshedToken);

    const data = await response.json();
    console.log('Response data received');
    return data;
  },

  post: async (endpoint, data, token = null) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const refreshedToken = response.headers.get('X-Refreshed-Token');
    if (refreshedToken) localStorage.setItem('gtrack_token', refreshedToken);

    return response.json();
  },

  put: async (endpoint, data, token = null) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const refreshedToken = response.headers.get('X-Refreshed-Token');
    if (refreshedToken) localStorage.setItem('gtrack_token', refreshedToken);

    return response.json();
  }
};

// Main App Component
const App = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  // Suppress the brief LoginScreen flash on F5 while we validate the saved token.
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.remove('theme-light');
    } else {
      document.body.classList.add('theme-light');
    }
  }, [isDarkMode]);

  // Check for existing login on app load
  useEffect(() => {
    const checkTokenValidity = async () => {
      try {
        const savedToken = localStorage.getItem('gtrack_token');
        const savedUser = localStorage.getItem('gtrack_user');

        if (savedToken && savedUser) {
          try {
            await api.get('/projects?sort_field=project_number&sort_direction=DESC', savedToken);
            setUser(JSON.parse(savedUser));
            setToken(savedToken);
            return;
          } catch (error) {
            localStorage.removeItem('gtrack_user');
            localStorage.removeItem('gtrack_token');
            console.log('Saved token expired, requiring fresh login');
          }
        }

        const savedUsername = localStorage.getItem('gtrack_username');
        const savedPassword = localStorage.getItem('gtrack_password');

        if (savedUsername && savedPassword) {
          try {
            const response = await api.post('/auth/login', { username: savedUsername, password: savedPassword });
            setUser(response.user);
            setToken(response.token);
            localStorage.setItem('gtrack_user', JSON.stringify(response.user));
            localStorage.setItem('gtrack_token', response.token);
          } catch {
            localStorage.removeItem('gtrack_username');
            localStorage.removeItem('gtrack_password');
          }
        }
      } finally {
        setCheckingAuth(false);
      }
    };

    checkTokenValidity();
  }, []);

  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('gtrack_user', JSON.stringify(userData));
    localStorage.setItem('gtrack_token', userToken);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('gtrack_username');
    localStorage.removeItem('gtrack_password');
    localStorage.removeItem('gtrack_user');
    localStorage.removeItem('gtrack_token');
  };

  return (
    <AlertProvider>
      {checkingAuth ? (
        <div className="loading-screen" style={{ minHeight: '100vh' }}>Loading…</div>
      ) : user ? (
        <ProjectList
          user={user}
          token={token}
          onLogout={handleLogout}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          api={api}
        />
      ) : (
        <LoginScreen onLogin={handleLogin} isDarkMode={isDarkMode} api={api} />
      )}
    </AlertProvider>
  );
};

export default App;